import React, { useState, useEffect } from 'react';

const LearningsTab = ({ apiUrl }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeSection, setActiveSection] = useState('insights');
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('all');

  const fetchLearnings = async (date) => {
    try {
      const dateParam = date && date !== 'all' ? `?date=${date}` : '';
      const url = `${apiUrl}/public/learnings${dateParam}`;
      console.log('[LearningsTab] 1. Fetching:', url);

      const response = await fetch(url);
      console.log('[LearningsTab] 2. Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      console.log('[LearningsTab] 3. Raw response length:', text.length);
      console.log('[LearningsTab] 4. First 200 chars:', text.substring(0, 200));

      let result;
      try {
        result = JSON.parse(text);
      } catch (parseErr) {
        console.error('[LearningsTab] JSON parse failed:', parseErr);
        throw new Error('Invalid JSON response');
      }

      console.log('[LearningsTab] 5. Parsed keys:', Object.keys(result));
      console.log('[LearningsTab] 6. success:', result.success);
      console.log('[LearningsTab] 7. learnings keys:', result.learnings ? Object.keys(result.learnings) : 'MISSING');
      console.log('[LearningsTab] 8. reflections count:', result.learnings?.reflections?.length);
      console.log('[LearningsTab] 9. availableDates:', result.availableDates?.length);

      if (result.success) {
        setData(result);
        setFetchError(null);

        // Normalize dates
        if (Array.isArray(result.availableDates)) {
          const dates = [];
          for (const d of result.availableDates) {
            if (!d) continue;
            if (typeof d === 'string') {
              dates.push(d.length > 10 ? d.substring(0, 10) : d);
            } else {
              try { dates.push(new Date(d).toISOString().split('T')[0]); } catch(e) { /* skip */ }
            }
          }
          setAvailableDates(dates);
          console.log('[LearningsTab] 10. Dates normalized:', dates.length);
        }
      } else {
        setFetchError(result.error || 'API returned success: false');
      }
    } catch (err) {
      console.error('[LearningsTab] FETCH ERROR:', err);
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('[LearningsTab] MOUNT — apiUrl:', apiUrl);
    fetchLearnings('all');
    const interval = setInterval(() => {
      fetchLearnings(selectedDate);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setLoading(true);
    fetchLearnings(date);
  };

  const navigateDate = (direction) => {
    if (availableDates.length < 2 || selectedDate === 'all') return;
    const idx = availableDates.indexOf(selectedDate);
    if (idx === -1) return;
    if (direction === 'older' && idx < availableDates.length - 1) handleDateChange(availableDates[idx + 1]);
    if (direction === 'newer' && idx > 0) handleDateChange(availableDates[idx - 1]);
  };

  const catIcon = (c) => {
    return { learning:'🧠', equilibrium:'⚖️', observation:'👁️', strategy:'🎯', warning:'⚠️', success:'✅', growth_pattern:'📈', issue_detection:'🔍' }[c] || '📝';
  };

  const fmtDate = (ds) => {
    if (!ds) return '--';
    try { return new Date(ds + 'T12:00:00').toLocaleDateString('en-US', { month:'numeric', day:'numeric', year:'numeric' }); }
    catch { return String(ds); }
  };

  const fmtUptime = (s) => {
    if (!s) return '--';
    return `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`;
  };

  const num = (v, fb) => { const x = parseFloat(v); return isNaN(x) ? (fb !== undefined ? fb : 0) : x; };

  // ---- LOADING ----
  if (loading && !data) {
    return (
      <div className="terminal-border rounded-lg p-8 text-center">
        <div className="text-lg terminal-glow">LOADING LEARNINGS...</div>
        <div className="text-xs text-[#00ff4180] mt-2">Fetching from {apiUrl}/public/learnings</div>
      </div>
    );
  }

  // ---- ERROR ----
  if (fetchError && !data) {
    return (
      <div className="terminal-border rounded-lg p-8 text-center">
        <div className="text-lg text-[#ff0000] mb-2">FETCH ERROR</div>
        <div className="text-sm text-[#ff000080] mb-4">{fetchError}</div>
        <div className="text-xs text-[#00ff4180] mb-4">URL: {apiUrl}/public/learnings</div>
        <button onClick={() => { setLoading(true); fetchLearnings('all'); }}
          className="px-4 py-2 bg-[#00ff41] text-[#0a0e1a] rounded font-bold">RETRY</button>
      </div>
    );
  }

  // ---- NO DATA ----
  if (!data) {
    return (
      <div className="terminal-border rounded-lg p-8 text-center">
        <div className="text-lg text-[#00ff4180]">NO DATA RECEIVED</div>
      </div>
    );
  }

  // ---- SAFE DESTRUCTURE ----
  const learnings = data.learnings || {};
  const vis = data.visualInspection || null;
  const cost = data.costStats || null;
  const sys = data.systemStats || null;
  const reflections = Array.isArray(learnings.reflections) ? learnings.reflections : [];
  const patterns = Array.isArray(learnings.patterns) ? learnings.patterns : [];
  const decisions = Array.isArray(learnings.recentDecisions) ? learnings.recentDecisions : [];

  console.log('[LearningsTab] RENDER — reflections:', reflections.length, 'patterns:', patterns.length, 'decisions:', decisions.length);

  // ---- MAIN RENDER ----
  return (
    <div className="space-y-6">

      {/* HEADER + DATE SELECTOR */}
      <div className="terminal-border rounded-lg p-4 scan-line">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-bold terminal-glow">🧠 — LEARNINGS DATABASE —</h2>
          <select
            value={selectedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            className="bg-[#0a0e1a] border border-[#00ff41] text-[#00ff41] rounded px-3 py-2 text-sm font-mono cursor-pointer"
            style={{ minWidth: '220px' }}
          >
            <option value="all">ALL TIME</option>
            {availableDates.map((ds, i) => (
              <option key={i} value={ds}>{fmtDate(ds)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* INFO BAR */}
      <div className="terminal-border rounded-lg overflow-hidden">
        <div className="px-4 py-2 flex items-center justify-between bg-[#00ff4108]">
          <div className="flex items-center gap-6 text-sm">
            <span>📅 {selectedDate === 'all' ? 'ALL TIME' : fmtDate(selectedDate)}</span>
            <span>🌱 Day {data.currentDay || '--'}</span>
            <span>📊 {(data.currentStage || 'unknown').toUpperCase()}</span>
          </div>
          {loading && <span className="text-[10px] text-[#00ff4180] animate-pulse">LOADING...</span>}
        </div>
      </div>

      {/* STATS ROW */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatBox label="INSIGHTS" value={reflections.length} />
        <StatBox label="PATTERNS" value={patterns.length} />
        <StatBox label="DECISIONS" value={decisions.length} />
        <StatBox label="INSPECTIONS" value={vis ? vis.totalInspections : 0} purple />
        <StatBox label="AVG HEALTH" value={vis && vis.averageHealth != null ? `${vis.averageHealth}%` : '--'} purple />
        <StatBox label="API SAVED" value={cost && cost.savings_percent != null ? `${cost.savings_percent}%` : '--'} />
      </div>

      {/* SECTION TABS */}
      <div className="flex gap-2 flex-wrap">
        {[
          ['insights', '💡 INSIGHTS'],
          ['decisions', '⚡ DECISIONS'],
          ['visual', '🔍 VISUAL'],
          ['costs', '💰 COSTS'],
          ['system', '🖥️ SYSTEM']
        ].map(([id, label]) => (
          <button key={id} onClick={() => setActiveSection(id)}
            className={`px-3 py-2 rounded text-xs font-bold transition-all ${
              activeSection === id
                ? 'bg-[#00ff41] text-[#0a0e1a]'
                : 'bg-[#00ff4120] text-[#00ff41] hover:bg-[#00ff4130]'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* ===== INSIGHTS ===== */}
      {activeSection === 'insights' && (
        <div className="space-y-4">
          {patterns.length > 0 && (
            <div className="terminal-border rounded-lg p-4">
              <h3 className="font-bold mb-3 terminal-glow">⚖️ EQUILIBRIUM PATTERNS</h3>
              <div className="space-y-2">
                {patterns.map((p, i) => (
                  <div key={i} className="bg-[#00ff4110] border border-[#00ff4140] rounded p-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold">{String(p.action || p.insight || 'Pattern')}</div>
                      {p.stage && <div className="text-[10px] text-[#00ff4180]">Stage: {p.stage}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold terminal-glow">{String(p.steady_cycles || p.count || '--')}</div>
                      <div className="text-[10px] text-[#00ff4180]">cycles</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="terminal-border rounded-lg p-4 scan-line">
            <h3 className="font-bold mb-3 terminal-glow">
              💡 INSIGHTS ({reflections.length})
            </h3>
            {reflections.length === 0 ? (
              <div className="text-center text-[#00ff4180] py-4">
                {selectedDate === 'all' ? 'No insights yet' : `No insights on ${fmtDate(selectedDate)}`}
              </div>
            ) : (
              <div className="space-y-3">
                {reflections.slice(0, 20).map((r, i) => (
                  <div key={i} className="bg-[#00ff4110] border border-[#00ff41] rounded p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{catIcon(r.category)}</span>
                        <span className="text-[10px] bg-[#00ff41] text-[#0a0e1a] px-2 py-0.5 rounded font-bold uppercase">
                          {String(r.category || 'insight')}
                        </span>
                      </div>
                      <div className="text-[10px] text-[#00ff4180]">
                        {r.timestamp ? new Date(r.timestamp).toLocaleString() : ''}
                      </div>
                    </div>
                    <div className="text-sm leading-relaxed">{String(r.insight || '--')}</div>
                    {r.evidence && (
                      <div className="text-[10px] text-[#00ff4140] mt-2 bg-[#0a0e1a] p-2 rounded border border-[#00ff4120]">
                        {typeof r.evidence === 'string' ? r.evidence : JSON.stringify(r.evidence)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== DECISIONS ===== */}
      {activeSection === 'decisions' && (
        <div className="terminal-border rounded-lg p-4 scan-line">
          <h3 className="font-bold mb-3 terminal-glow">⚡ AI DECISIONS ({decisions.length})</h3>
          {decisions.length === 0 ? (
            <div className="text-center text-[#00ff4180] py-4">No decisions recorded</div>
          ) : (
            <div className="space-y-2">
              {decisions.map((d, i) => (
                <div key={i} className="border-l-2 border-[#00ff41] pl-3 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] text-[#00ff4180]">
                      {d.timestamp ? new Date(d.timestamp).toLocaleString() : ''}
                    </div>
                    <div className="flex gap-1">
                      {d.applied && (
                        <span className="text-[10px] bg-[#00ff41] text-[#0a0e1a] px-1.5 py-0.5 rounded font-bold">APPLIED</span>
                      )}
                      {d.confidence && (
                        <span className="text-[10px] bg-[#00ff4120] text-[#00ff41] px-1.5 py-0.5 rounded">{String(d.confidence)}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm">{String(d.summary || d.action || '--')}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== VISUAL ===== */}
      {activeSection === 'visual' && (
        <div className="space-y-4">
          {!vis ? (
            <div className="terminal-border rounded-lg p-8 text-center" style={{ borderColor: '#9c27b040' }}>
              <div className="text-3xl mb-2">🔍</div>
              <div className="text-sm text-[#00ff4180]">No visual inspections</div>
            </div>
          ) : (
            <div className="terminal-border rounded-lg p-4" style={{ borderColor: '#9c27b0' }}>
              <h3 className="font-bold mb-3" style={{ color: '#9c27b0' }}>📊 HEALTH OVERVIEW</h3>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <PurpleStat label="LATEST" value={vis.latestHealth != null ? `${vis.latestHealth}%` : '--'} />
                <PurpleStat label="AVERAGE" value={vis.averageHealth != null ? `${vis.averageHealth}%` : '--'} />
                <PurpleStat label="TOTAL" value={String(vis.totalInspections || 0)} />
                <PurpleStat label="ISSUES" value={String(vis.issuesFound || 0)} warn />
              </div>

              {Array.isArray(vis.recentInspections) && vis.recentInspections.map((insp, i) => (
                <div key={i} className="flex items-center gap-3 py-1.5 border-t border-[#9c27b020]">
                  <div className="text-[10px] text-[#00ff4180] w-16">Day {insp.day || '--'}</div>
                  <div className="flex-1 h-2 bg-[#00ff4120] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{
                      width: `${num(insp.health, 0)}%`,
                      backgroundColor: num(insp.health, 0) > 80 ? '#00ff41' : num(insp.health, 0) > 60 ? '#ffeb3b' : '#ff0000'
                    }} />
                  </div>
                  <div className="text-xs font-bold w-10 text-right">{insp.health != null ? `${insp.health}%` : '--'}</div>
                </div>
              ))}

              {Array.isArray(vis.recentInspections) && vis.recentInspections.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="font-bold text-sm" style={{ color: '#9c27b0' }}>🔍 SUMMARIES</h4>
                  {vis.recentInspections.map((insp, i) => (
                    <div key={i} className="bg-[#9c27b010] border border-[#9c27b040] rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-[#00ff4180]">
                          Day {insp.day || '--'} · {(insp.stage || '').toUpperCase()} · {insp.timestamp ? new Date(insp.timestamp).toLocaleDateString() : '--'}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                          insp.urgency === 'critical' || insp.urgency === 'high' ? 'bg-red-600 text-white'
                          : insp.urgency === 'medium' ? 'bg-[#ffa500] text-white'
                          : 'bg-[#00ff41] text-[#0a0e1a]'
                        }`}>
                          {(insp.urgency || 'low').toUpperCase()}
                        </span>
                      </div>
                      <div className="text-xs">{String(insp.summary || 'No summary')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== COSTS ===== */}
      {activeSection === 'costs' && (
        <div className="space-y-4">
          {!cost ? (
            <div className="terminal-border rounded-lg p-8 text-center">
              <div className="text-sm text-[#00ff4180]">No cost data</div>
            </div>
          ) : (
            <div className="terminal-border rounded-lg p-4">
              <h3 className="font-bold mb-3 terminal-glow">💰 API COSTS</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <CostCard label={selectedDate === 'all' ? 'TODAY' : fmtDate(selectedDate)}
                  value={'$' + num(cost.today_cost).toFixed(3)} sub={num(cost.today_calls) + ' calls'} />
                <CostCard label="TOTAL" value={'$' + num(cost.total_cost).toFixed(2)} sub={num(cost.total_calls) + ' calls'} />
                <CostCard label="SKIPPED" value={String(num(cost.skipped_calls))} sub={num(cost.savings_percent) + '% savings'} />
                <CostCard label="EST/MONTH"
                  value={num(cost.active_days) > 0 ? '$' + (num(cost.total_cost) / num(cost.active_days, 1) * 30).toFixed(2) : '--'}
                  sub="at current rate" />
              </div>
              <div className="space-y-1 text-sm">
                <CostRow label="Haiku decisions" cost="~$0.001" />
                <CostRow label="Deadband skips" cost="FREE" green />
                <CostRow label="Sonnet reports" cost="~$0.02" />
                <CostRow label="Sonnet vision" cost="~$0.03" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== SYSTEM ===== */}
      {activeSection === 'system' && (
        <div className="terminal-border rounded-lg p-4">
          <h3 className="font-bold mb-3 terminal-glow">🖥️ SYSTEM</h3>
          {sys ? (
            <div className="space-y-2 text-sm">
              <SysRow label="Uptime" value={fmtUptime(sys.uptime)} />
              <SysRow label="Controller"
                value={sys.controllerConnected ? '✓ CONNECTED' : '✗ SIMULATION'}
                cls={sys.controllerConnected ? 'text-[#00ff41]' : 'text-[#ff0000]'} />
              <SysRow label="AI Mode" value={(sys.aiMode || '--').toUpperCase()} />
              <SysRow label="Decisions" value={String(sys.decisionCount || 0)} />
              <SysRow label="Grow" value={`${sys.strain || 'Unknown'} — Day ${sys.currentDay || '--'}`} />
              <SysRow label="Stage" value={(sys.currentStage || '--').toUpperCase()} />
            </div>
          ) : (
            <div className="text-center text-[#00ff4180] py-4">Unavailable</div>
          )}
        </div>
      )}

      {/* DAY NAVIGATION */}
      {selectedDate !== 'all' && availableDates.length > 1 && (
        <div className="flex items-center justify-between text-xs">
          <button onClick={() => navigateDate('older')}
            className="px-3 py-1 rounded border border-[#00ff41] text-[#00ff41] hover:bg-[#00ff4120]">← OLDER</button>
          <button onClick={() => handleDateChange('all')}
            className="px-3 py-1 rounded border border-[#00ff41] text-[#00ff41] hover:bg-[#00ff4120]">VIEW ALL</button>
          <button onClick={() => navigateDate('newer')}
            className="px-3 py-1 rounded border border-[#00ff41] text-[#00ff41] hover:bg-[#00ff4120]">NEWER →</button>
        </div>
      )}
    </div>
  );
};

// ---- TINY SUB-COMPONENTS (crash-proof, no complex logic) ----

const StatBox = ({ label, value, purple }) => (
  <div className="terminal-border rounded-lg p-3 text-center"
    style={purple ? { borderColor: '#9c27b0' } : {}}>
    <div className="text-2xl font-bold"
      style={purple ? { color: '#9c27b0' } : { color: '#00ff41', textShadow: '0 0 10px rgba(0,255,65,0.5)' }}>
      {String(value)}
    </div>
    <div className="text-[10px] text-[#00ff4180] mt-1">{label}</div>
  </div>
);

const PurpleStat = ({ label, value, warn }) => (
  <div className="bg-[#9c27b010] border border-[#9c27b040] rounded p-2 text-center">
    <div className="text-[10px] text-[#00ff4180]">{label}</div>
    <div className={`text-lg font-bold ${warn ? 'text-[#ffa500]' : ''}`}
      style={!warn ? { color: '#9c27b0' } : {}}>
      {String(value)}
    </div>
  </div>
);

const CostCard = ({ label, value, sub }) => (
  <div className="bg-[#00ff4110] border border-[#00ff4140] rounded p-3 text-center">
    <div className="text-[10px] text-[#00ff4180]">{label}</div>
    <div className="text-xl font-bold terminal-glow">{String(value)}</div>
    <div className="text-[10px] text-[#00ff4140]">{sub}</div>
  </div>
);

const CostRow = ({ label, cost, green }) => (
  <div className="flex justify-between py-1 border-b border-[#00ff4120]">
    <span className="text-[#00ff4180]">{label}</span>
    <span className={green ? 'text-[#00ff41]' : ''}>{cost}</span>
  </div>
);

const SysRow = ({ label, value, cls }) => (
  <div className="flex justify-between py-1.5 border-b border-[#00ff4120]">
    <span className="text-[#00ff4180]">{label}</span>
    <span className={`font-bold ${cls || ''}`}>{String(value)}</span>
  </div>
);

export default LearningsTab;
